import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useState, useEffect, useRef } from 'react';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';

const InputBox = ({
  id = 'priButton',
  name = 'mainName',
  type = 'text',
  onBlur = () => {},
  onChange = () => {},
  isDisable,
  className = '',
  value = '',
  placeholder,
  label,
  infoTxt,
  isTextArea = '',
  isTextAreaFor = false,
  defaultFields = null,
  autocomplete = true,
  step = 0,
  min = 0,
  max = 0,
  initHeight = 0,
  autoResize = false,
  disableResize = false,
  initialResize = true,
  ...rest
}) => {
  const { isLanguage } = useSelector((state) => state.login);

  // Helper function to format height value
  const formatHeight = (heightValue) => {
    if (!heightValue) return 'auto';
    if (typeof heightValue === 'string' && heightValue.includes('%')) {
      return heightValue;
    }
    if (typeof heightValue === 'number') {
      return `${heightValue}px`;
    }
    return heightValue;
  };

  const [height, setHeight] = useState(formatHeight(initHeight));
  const [isTextAreaMode, setIsTextAreaMode] = useState(isTextArea);
  const [isInitialized, setIsInitialized] = useState(false);
  const textareaRef = useRef(null);
  const inputRef = useRef(null);

  // Calculate height - runs on initial mount (if initialResize) or when value changes (if autoResize is true)
  useEffect(() => {
    const shouldCalculateHeight =
      autoResize || (initialResize && !isInitialized);

    if (textareaRef.current && shouldCalculateHeight) {
      const el = textareaRef.current;

      // Reset height to auto so scrollHeight is measured correctly
      el.style.height = 'auto';
      const scrollHeight = el.scrollHeight;

      // Parse initHeight into pixels
      let baseHeight = 0;
      if (typeof initHeight === 'number') {
        baseHeight = initHeight;
      } else if (typeof initHeight === 'string') {
        if (initHeight.endsWith('px')) {
          baseHeight = parseInt(initHeight, 10);
        } else if (initHeight.endsWith('%')) {
          const parent = el.parentElement;
          if (parent) {
            baseHeight = (parent.clientHeight * parseInt(initHeight, 10)) / 100;
          }
        } else if (initHeight.endsWith('vh')) {
          baseHeight = (window.innerHeight * parseInt(initHeight, 10)) / 100;
        } else if (initHeight.endsWith('vw')) {
          baseHeight = (window.innerWidth * parseInt(initHeight, 10)) / 100;
        }
      }

      // Pick the larger of scrollHeight vs min initHeight
      let newHeight = Math.max(scrollHeight, baseHeight);

      // Add extra padding to prevent scrollbar (add 4px buffer)
      newHeight += 4;

      el.style.height = `${newHeight}px`;
      setHeight(`${newHeight}px`);

      if (!isInitialized) {
        setIsInitialized(true);
      }
    } else if (!initialResize && !isInitialized) {
      // If initialResize is false, just use initHeight directly
      setHeight(formatHeight(initHeight));
      setIsInitialized(true);
    }
  }, [initHeight, isInitialized, autoResize, initialResize, value]);

  const inputClass = classNames(className, {
    inputStyle: true,
    'input-disabled': isDisable,
    'Txt-REV': isLanguage === 'ARB',
  });

  const lablStyle = classNames(className, {
    lblTxt: true,
    'lblTxt-REV': isLanguage === 'ARB',
  });

  const infoStyle = classNames(className, {
    infTxt: true,
    'lblTxt-REV': isLanguage === 'ARB',
  });

  return (
    <div>
      {label ? <div className={lablStyle}>{label}</div> : null}
      {type === 'date' ? (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            sx={{ padding: '0' }}
            value={value ? moment(value, 'MMM DD YYYY', true) : null}
            onChange={(newValue) => {
              onChange(moment(newValue).format('MMM DD YYYY'));
            }}
            format="MMM DD YYYY"
            disabled={isDisable}
            slotProps={{
              textField: {
                fullWidth: true,
                className: 'ccscsc',
                sx: {
                  padding: '0',
                  fontSize: '12px!important',
                  borderColor: '#d3d9de',
                  '& .MuiOutlinedInput-root': {
                    '& .MuiInputBase-input': {
                      padding: '18.50px!important',
                      fontSize: '12px!important',
                    },
                    '& fieldset': {
                      borderColor: '#d3d9de!important',
                    },
                  },
                  '& .MuiSvgIcon-root': {
                    width: '24px!important',
                    height: '24px!important',
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
      ) : isTextArea ||
        (defaultFields && defaultFields !== 'recommendation_amount') ? (
        <textarea
          ref={textareaRef}
          key={id}
          onBlur={onBlur}
          disabled={isDisable}
          onChange={(e) => {
            onChange(e);
            // Scroll cursor into view when typing
            if (autoResize || initialResize) {
              requestAnimationFrame(() => {
                if (textareaRef.current) {
                  const cursorPosition = textareaRef.current.selectionStart;
                  const textBeforeCursor = textareaRef.current.value.substring(
                    0,
                    cursorPosition
                  );
                  const lines = textBeforeCursor.split('\n').length;
                  const lineHeight =
                    parseInt(
                      window.getComputedStyle(textareaRef.current).lineHeight
                    ) || 20;
                  const cursorTop = lines * lineHeight;

                  // Scroll the textarea to show the cursor with some padding
                  textareaRef.current.scrollTop = Math.max(
                    0,
                    cursorTop -
                      textareaRef.current.clientHeight +
                      lineHeight * 2
                  );
                }
              });
            }
          }}
          className={inputClass}
          value={value}
          placeholder={placeholder}
          style={{
            height: isTextAreaFor ? '100px' : height,
            resize: disableResize ? 'none' : 'vertical',
          }}
          {...rest}
        />
      ) : (
        <input
          ref={inputRef}
          key={id}
          onBlur={onBlur}
          type={type ? type : 'text'}
          disabled={isDisable}
          onChange={(e) => {
            onChange(e);
            if (e.target.value.length > 30) {
              setIsTextAreaMode(true);
            }
          }}
          onWheel={(e) => e.target.blur()}
          className={inputClass}
          value={value}
          placeholder={placeholder}
          {...rest}
        />
      )}
      {infoTxt ? <div className={infoStyle}>{infoTxt}</div> : null}
    </div>
  );
};

export default InputBox;
